import React from 'react';
import { AuditResultV2, fullPageModalLayer } from '@scale/shared/defaults';
import SpeedAuditFeedback from 'react-scale/components/audits/SpeedAudit/v2/SpeedAuditFeedback';
import IconToggle from 'design-system/IconToggle';
import {
  SpeedAuditLookup,
  SpeedAuditResult,
  SubtaskAssignment,
} from 'react-scale/components/audits/SpeedAudit/types';
import { Actions } from 'react-use-v13/lib/useMap';
import { PartialRecord } from '@scale/shared/types/utils';
import { makeStyles } from 'design-system';
import { getInitialResult } from '../utils';
import { isValidObjectId } from 'react-scale/components/design-system/ObjectIdInput/ObjectIdInput';
import { IUserJson } from '@scale/shared/types/user';
interface IObjectNavigationProps {
  subtaskAssignment: SubtaskAssignment | undefined;
  speedAuditResult: SpeedAuditResult | undefined;
  currentAttemptAudit: SpeedAuditLookup | undefined;
  selectedAttempt: string | undefined;
  triggerShortcutV2: boolean;
  triggerShortcutValueV2: AuditResultV2;
  projectId: string;
  isCompleted: boolean;
  index: number;
  numObjects: number;
  numSubtaskAssignments: number;
  isMissingRequiredGrade: boolean;
  onPrevUnaudited: () => void;
  onNextUnaudited: () => void;
  onPrev: () => void;
  onNext: () => void;
  speedAuditResultsActions: Actions<PartialRecord<string, SpeedAuditResult>>;
  user: IUserJson;
  setIsWritingFeedback?: (isWriting: boolean) => void;
}

const useStyles = makeStyles({
  feedback: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    background: 'white',
    position: 'fixed',
    left: '0',
    bottom: '0',
    boxSizing: 'border-box',
    width: '100%',
    zIndex: fullPageModalLayer,
    borderTop: '1px solid #ccc',
    boxShadow: '2px -2px 2px #eee',
  },
  navArrows: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    '& > *:not(:first-child)': {
      marginLeft: 4,
    },
  },
});

export const SpeedAuditV2ObjectNavigation: React.FC<IObjectNavigationProps> = props => {
  const styles = useStyles();
  const { subtaskAssignment, speedAuditResult, currentAttemptAudit, selectedAttempt, user } = props;
  const { triggerShortcutV2, triggerShortcutValueV2, projectId, isCompleted, index } = props;
  const {
    numObjects,
    numSubtaskAssignments,
    isMissingRequiredGrade,
    speedAuditResultsActions,
    setIsWritingFeedback,
  } = props;
  const { onPrev, onPrevUnaudited, onNext, onNextUnaudited } = props;
  const onAuditUpdate = React.useCallback(
    (attemptAudit: SpeedAuditLookup) => {
      const { resultV2 } = attemptAudit;
      const objectId = attemptAudit.auditLookup.id;
      const prev = speedAuditResultsActions.get(objectId) || getInitialResult(attemptAudit as any);
      speedAuditResultsActions.set(objectId, { ...prev, resultV2 });
    },
    [speedAuditResultsActions],
  );

  return (
    <>
      <div style={{ height: '100px' }} />
      <div className={styles.feedback}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {subtaskAssignment &&
            speedAuditResult &&
            currentAttemptAudit?._id &&
            currentAttemptAudit?.auditLookup?.id &&
            selectedAttempt &&
            isValidObjectId(selectedAttempt) && (
              <SpeedAuditFeedback
                triggerShortcut={triggerShortcutV2}
                triggerShortcutValue={triggerShortcutValueV2}
                attemptAuditId={currentAttemptAudit._id}
                attemptId={selectedAttempt}
                objectId={currentAttemptAudit?.auditLookup?.id}
                projectId={projectId}
                key={selectedAttempt}
                subtaskAssignment={subtaskAssignment}
                onAuditUpdate={onAuditUpdate}
                user={user}
                setIsWritingFeedback={setIsWritingFeedback}
              />
            )}
          {selectedAttempt && !isValidObjectId(selectedAttempt) && (
            <div style={{ color: 'red' }}>
              Grading/adding error categories for consensus responses is not currently supported.
            </div>
          )}
        </div>

        <div style={{ flexGrow: 1, marginRight: '100px' }} />
        <div>
          <div className={styles.navArrows}>
            <IconToggle
              name="previous-unaudited-object"
              icon="arrow-left"
              isOn
              isDisabled={index === 0}
              onClick={onPrevUnaudited}
              tooltip="Skip to Previous Unaudited Object"
            />
            <IconToggle
              name="previous-object"
              icon="chevron-left"
              isOn
              isDisabled={index === 0}
              onClick={onPrev}
              tooltip="Previous Object"
            />
            <div>
              {index + 1} / {numObjects} Audits
              <br />(
              {numObjects === numSubtaskAssignments
                ? 'Done loading'
                : `Still loading ${numObjects - numSubtaskAssignments} Audits`}
              )
            </div>
            <IconToggle
              name="next-object"
              icon="chevron-right"
              isOn
              isDisabled={isCompleted || isMissingRequiredGrade}
              onClick={onNext}
              tooltip={
                isMissingRequiredGrade
                  ? 'You must select a grade to continue to Next Object'
                  : 'Next Object'
              }
            />
            <IconToggle
              name="next-unaudited-object"
              icon="arrow-right"
              isOn
              isDisabled={isCompleted || isMissingRequiredGrade}
              onClick={onNextUnaudited}
              tooltip={
                isMissingRequiredGrade
                  ? 'You must select a grade to continue to Skip to Next Unaudited Object'
                  : 'Skip to Next Unaudited Object'
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};
